import { Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLeveragedCefs } from '@/hooks/useLeveragedCefs';

/**
 * Round P136 (Feb 2026) — Watchlist mirror of the Portfolio leverage
 * concentration banner. Renders when > 30% of watchlist tickers are
 * structurally-leveraged CEFs. Silent no-op below that threshold or
 * when the shared hook hasn't loaded yet.
 *
 * Props:
 *   • watchlist — array of ticker strings (from watchlistStore)
 *   • stocks    — the current stock docs (for is_cef check); optional,
 *                 falls back to "trust the curated set" if not passed
 */
export default function WatchlistLeverageCallout({ watchlist, stocks }) {
  const levSet = useLeveragedCefs();
  if (!Array.isArray(watchlist) || watchlist.length === 0) return null;
  if (levSet.size === 0) return null;

  const cefLookup = new Map(
    Array.isArray(stocks) ? stocks.map((s) => [s.ticker, !!s.is_cef]) : [],
  );

  const levTickers = watchlist.filter((t) => {
    if (!t) return false;
    if (!levSet.has(String(t).toUpperCase())) return false;
    // If we have stock metadata, require is_cef=true; otherwise trust
    // the curated set (all entries are CEFs by construction).
    if (cefLookup.size > 0) {
      return cefLookup.get(t) === true;
    }
    return true;
  });

  const total = watchlist.length;
  if (levTickers.length === 0) return null;
  const pct = (levTickers.length / total) * 100;
  if (pct <= 30) return null;

  return (
    <div
      data-testid="watchlist-leverage-callout"
      className="flex items-start gap-3 rounded-lg border border-amber-500/60 bg-amber-50 dark:bg-amber-950/30 px-3 py-2.5 text-sm mb-3"
    >
      <Zap className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
      <div className="min-w-0 text-amber-900 dark:text-amber-100">
        <span className="font-semibold">
          {levTickers.length} of {total} watchlist tickers ({pct.toFixed(0)}%)
        </span>{' '}
        use structural leverage — {levTickers.slice(0, 5).join(', ')}
        {levTickers.length > 5 ? `, +${levTickers.length - 5} more` : ''}.
        These CEFs borrow (typically 25-45%) to boost yield, which amplifies both distributions AND drawdowns. Consider diversifying before committing capital.
        {' '}
        <Link
          to="/glossary/cef-strategies"
          className="underline underline-offset-2 hover:text-amber-700 dark:hover:text-amber-300"
        >
          See the Leverage lens →
        </Link>
      </div>
    </div>
  );
}
